import React from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  InputAdornment,
  TextField,
  TextareaAutosize,
  styled,
} from "@mui/material";
import { Controller } from "react-hook-form";
import dataConstant from "../../constants/dataConstants";
import { messageConstant } from "../../constants/messageConstant";

const CustomTextFieldStyle = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& .MuiInputBase-input": {
      paddingTop: "15.5px",
      paddingBottom: "15.5px",
    },
    "& .MuiInputBase-input:disabled": {
      color: "var(--black)",
      WebkitTextFillColor: "var(--black) !important",
      cursor: "no-drop",
    },
    "& fieldset": {
      border: "none",
    },
    "& Mui-error": {
      position: "absolute",
      left: "0",
    },
  },
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    fontSize: "0.75rem",
    color: "var(--secondary-text)",
    marginBottom: theme.spacing(0.5),
  },
}));

const CustomTextField = ({ properties, methods }) => {
  const {
    inputRef,
    name,
    label = "",
    placeholder,
    type = "text",
    isPasswordText = false,
    minLength,
    maxLength,
    minLengthMessage,
    min,
    max,
    step,
    digitsBeforeDecimal,
    digitsAfterDecimal,
    isTextarea,
    minRows,
    maxRows,
    required,
    isDisabled,
    readOnly,
    validation,
    regex,
    inputAdornment,
    endInputAdornment,
    customOnBlur,
    onCustomChange,
    className = "",
    capitalize = false,
    showCharCount = false,
    tabIndex=null,
  } = properties;

  const { control } = methods;
  const { t } = useTranslation();

  const getDecimalRegex = () => {
    const digitsBefore =
      digitsBeforeDecimal && digitsAfterDecimal
        ? digitsBeforeDecimal - digitsAfterDecimal
        : digitsAfterDecimal
        ? dataConstant.DefaultDigitsBeforeDecimal - digitsAfterDecimal
        : dataConstant.DefaultDigitsBeforeDecimal -
          dataConstant.NumericFixedDigit;
    const digitsAfter = digitsAfterDecimal ?? dataConstant.NumericFixedDigit;
    return new RegExp(`^\\d{1,${digitsBefore}}(\\.\\d{0,${digitsAfter}})?$`);
  };

  const customRules = {
    ...validation,
    required: {
      value: required || false,
      message:
        label.charAt(0).toUpperCase() +
        label.slice(1).toLowerCase() +
        " " +
        t("Common.Is Required"),
    },
    pattern:
      type === dataConstant.PasswordType || isPasswordText
        ? {
            value: dataConstant.Pattern.PasswordRegex,
            message: messageConstant.InvalidPasswordPattern,
          }
        : type === "phone"
        ? {
            value: dataConstant.Pattern.PhoneRegex,
            message: messageConstant.EnterValid(label),
          }
        : type === "email"
        ? {
            value: dataConstant.Pattern.EmailRegex,
            message: messageConstant.EnterValid(label),
          }
        : regex
        ? { value: regex, message: messageConstant.EnterValid(label) }
        : undefined,
    maxLength: maxLength
      ? {
          value:
            type === "phone" ? dataConstant.DefaultMaxPhoneLength : maxLength,
          message: label
            ? type === "number" || type === "naturalNumber" || type === "phone"
              ? messageConstant.MaxNumberLength(
                  label,
                  type === "phone"
                    ? dataConstant.DefaultMaxPhoneLength
                    : maxLength
                )
              : messageConstant.MaxCharacterLength(label, maxLength)
            : "",
        }
      : undefined,
    minLength: minLength
      ? {
          value:
            type === "phone" ? dataConstant.DefaultMinPhoneLength : minLength,
          message: label
            ? type === "number" || type === "naturalNumber" || type === "phone"
              ? minLengthMessage
                ? minLengthMessage
                : messageConstant.MinNumberLength(
                    label,
                    type === "phone"
                      ? dataConstant.DefaultMinPhoneLength
                      : minLength
                  )
              : messageConstant.MinNumberLength(label, minLength)
            : "",
        }
      : undefined,
    min:
      min !== undefined
        ? {
            value: min,
            message: label ? messageConstant.MinNumberValue(label, min) : "",
          }
        : undefined,
    max:
      max !== undefined
        ? {
            value: max,
            message: label ? messageConstant.MaxNumberValue(label, max) : "",
          }
        : undefined,
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, value, ref, onBlur },
          fieldState: { error },
        }) => (
          <>
            <FormControl fullWidth>
              <CustomTextFieldStyle
                ref={inputRef}
                inputRef={ref}
                onBlur={(e) => {
                  const trimmedValue = e.target.value.trim();
                  customOnBlur && customOnBlur(trimmedValue);
                  onChange(trimmedValue);
                }}
                size={"medium"}
                variant={"outlined"}
                type={type === "naturalNumber" ? "number" : type}
                disabled={isDisabled}
                placeholder={placeholder}
                required={required}
                id={`${name}-label`}
                label={label}
                value={value ?? ""}
                error={!!error}
                className={className}
                helperText={
                  <>
                    {error?.message && <span>{error?.message}</span>}
                    {type === "text" && showCharCount && maxLength && (
                      <span className="cm-char-count">{`${
                        value?.toString()?.length || 0
                      }/${maxLength}`}</span>
                    )}
                  </>
                }
                onChange={(e) => {
                  let inputValue = e.target.value;

                  if (capitalize) {
                    inputValue = inputValue.toUpperCase();
                  }

                  if (type === "number") {
                    const decimalRegex = getDecimalRegex();
                    if (!inputValue || decimalRegex.test(inputValue)) {
                      onChange(inputValue);
                    }
                  } else {
                    onChange(inputValue);
                  }

                  onCustomChange && onCustomChange(inputValue);

                  if (!inputValue) {
                    onChange("");
                  }
                }}
                onInput={(e) => {
                  if (type === "naturalNumber") {
                    if (e.target?.value) {
                      e.target.value = e.target.value.replace(
                        dataConstant.Pattern.OnlyNumberRegex,
                        ""
                      );
                    }
                    onChange(e);
                  }
                }}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    ...(isTextarea
                      ? {
                          style: { resize: "auto" },
                          minRows: minRows ?? 4,
                          maxRows: maxRows ?? 4,
                          readOnly: readOnly,
                        }
                      : {}),
                    ...(step && type === "number"
                      ? { step: step ?? 0.01 }
                      : type === dataConstant.PasswordType
                      ? { autoComplete: "new-password" }
                      : {}),
                    ...(tabIndex !== null ? { tabIndex: tabIndex } : {}),
                  },
                  input: {
                    ...(isTextarea
                      ? {
                          inputComponent: TextareaAutosize,
                        }
                      : {}),
                    ...(inputAdornment
                      ? {
                          startAdornment: (
                            <InputAdornment position="start">
                              {inputAdornment}
                            </InputAdornment>
                          ),
                        }
                      : {}),
                    ...(endInputAdornment
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              {endInputAdornment}
                            </InputAdornment>
                          ),
                        }
                      : {}),
                    ...(readOnly ? { readOnly: readOnly } : {}),
                  },
                }}
              />
            </FormControl>
          </>
        )}
        rules={customRules}
      />
    </>
  );
};

export default CustomTextField;